(() => {
  const instances = new Map();
  const clamp = value => Math.max(0, Math.min(1, value));
  const mix = (a, b, t) => a + (b - a) * t;
  const ease = t => t * t * (3 - 2 * t);

  function kyniqoScroller(node) {
    let el = node?.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const overflow = getComputedStyle(el).overflowY;
      if ((overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return window;
  }
  function kyniqoMetrics(scroller) {
    if (scroller === window) return { viewport: window.innerHeight, origin: 0 };
    return { viewport: scroller.clientHeight, origin: scroller.getBoundingClientRect().top };
  }
  function init(gallery) {
    if (instances.has(gallery)) return;
    const cards = [...gallery.querySelectorAll('[data-game-card]')];
    const finaleImages = gallery.querySelectorAll('[data-games-finale] img');
    const finaleCards = [...gallery.querySelectorAll('[data-games-finale-card]')];
    const stage = gallery.querySelector('[data-games-stage]');
    const status = gallery.querySelector('[data-games-status]');
    if (!cards.length) return;
    const controller = new AbortController();
    const { signal } = controller;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const finaleLayout = new Map();
    let lastSurfaceSettle = NaN;
    const segmentCount = 9;
    const scroller = kyniqoScroller(gallery);

    finaleCards.forEach(card => {
      const image = card.querySelector('img');
      if (!image) return;
      const surface = document.createElement('div');
      surface.className = card.classList.contains('kyniqo-games-finale-card')
        ? 'kyniqo-games-finale-surface'
        : 'games-finale-surface';
      surface.setAttribute('aria-hidden', 'true');
      surface.style.setProperty('--float-delay', image.style.getPropertyValue('--float-delay'));
      for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
        const segment = document.createElement('span');
        segment.className = card.classList.contains('kyniqo-games-finale-card')
          ? 'kyniqo-games-finale-segment'
          : 'games-finale-segment';
        segment.style.backgroundImage = `url(${JSON.stringify(image.getAttribute('src'))})`;
        segment.style.height = `${100 / segmentCount}%`;
        segment.style.backgroundSize = `100% ${segmentCount * 100}%`;
        segment.style.backgroundPosition = `center ${segmentIndex * 100 / (segmentCount - 1)}%`;
        surface.append(segment);
      }
      card.append(surface);
      card.classList.add('is-curved');
    });

    function layoutFinale() {
      if (!stage || !finaleCards.length) return;
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      const mobile = width < 700;
      const columns = mobile ? 6 : 9;
      const rows = Math.ceil(finaleCards.length / columns);
      const cardWidth = mobile ? Math.min(58, width * .13) : Math.min(108, width * .071);
      const cardHeight = cardWidth * 4 / 3;
      const stepX = cardWidth * 1.12;
      const rowGap = mobile ? 12 : 15;
      const rowStep = cardHeight + rowGap;
      const bendStart = mobile ? -60 : -55;
      const bendLength = mobile ? cardHeight * 2.35 : cardHeight * 1.62;
      const depth = mobile ? -165 : 0;
      const perspective = 950;
      const segmentHeight = cardHeight / segmentCount;

      function geometryAt(degrees, surfaceDepth = depth, start = bendStart, length = bendLength) {
        const bendAngle = degrees * Math.PI / 180;
        const curvature = bendAngle / length;
        const pointOnSurface = distance => {
          if (distance <= start) return { y: distance - start, z: 0, angle: 0 };
          const intoBend = Math.min(distance - start, length);
          const angle = intoBend * curvature;
          const y = Math.sin(angle) / curvature;
          const z = (1 - Math.cos(angle)) / curvature;
          if (distance <= start + length) return { y, z, angle };
          const afterBend = distance - start - length;
          return {
            y: y + afterBend * Math.cos(bendAngle),
            z: z + afterBend * Math.sin(bendAngle),
            angle: bendAngle
          };
        };
        const projectedY = (distance, originY) => {
          const point = pointOnSurface(distance);
          return height / 2 + (originY + point.y) * perspective / (perspective - surfaceDepth - point.z);
        };
        const firstDistance = -(rows - 1) * rowStep / 2;
        let centerY = 0;
        let rowDistances = [];
        for (let pass = 0; pass < 3; pass++) {
          rowDistances = [firstDistance];
          for (let row = 1; row < rows; row++) {
            const previous = rowDistances[row - 1];
            const previousBottom = projectedY(previous + cardHeight / 2, centerY);
            let low = previous + cardHeight;
            let high = low + cardHeight * 3;
            for (let search = 0; search < 22; search++) {
              const middle = (low + high) / 2;
              if (projectedY(middle - cardHeight / 2, centerY) - previousBottom < rowGap) low = middle;
              else high = middle;
            }
            rowDistances.push((low + high) / 2);
          }
          const first = pointOnSurface(rowDistances[0] - cardHeight / 2);
          const last = pointOnSurface(rowDistances[rows - 1] + cardHeight / 2);
          const firstScale = perspective / (perspective - surfaceDepth - first.z);
          const lastScale = perspective / (perspective - surfaceDepth - last.z);
          const visualCenter = height * (mobile ? .54 : .55);
          centerY = (2 * (visualCenter - height / 2) - first.y * firstScale - last.y * lastScale) / (firstScale + lastScale);
        }
        return finaleCards.map((card, index) => {
          const row = Math.floor(index / columns);
          const column = index % columns;
          const x = (column - (columns - 1) / 2) * stepX;
          const distance = rowDistances[row];
          const center = pointOnSurface(distance);
          const segments = Array.from({ length: segmentCount }, (_, part) => {
            const segmentDistance = distance + (part - (segmentCount - 1) / 2) * segmentHeight;
            const point = pointOnSurface(segmentDistance);
            return {
              y: point.y - center.y - segmentHeight / 2,
              z: point.z - center.z,
              angle: point.angle * 180 / Math.PI
            };
          });
          return { x, y: centerY + center.y, z: surfaceDepth + center.z, segments };
        });
      }

      const expanded = geometryAt(mobile ? 76 : 84, mobile ? depth : -120);
      const settled = geometryAt(78);
      finaleCards.forEach((card, index) => {
        const row = Math.floor(index / columns);
        if (!mobile && row === rows - 1) expanded[index].y += 35;
        card.style.width = `${cardWidth}px`;
        card.style.height = `${cardHeight}px`;
        finaleLayout.set(card, {
          expanded: expanded[index],
          settled: settled[index],
          segments: [...card.querySelectorAll('[class$="games-finale-segment"]')]
        });
        card.style.opacity = '1';
        card.style.zIndex = String(row + 1);
        finaleLayout.get(card).segments.forEach(segment => {
          segment.style.opacity = String(mobile ? Math.min(1, .72 + row * .055) : Math.min(1, .74 + row * .085));
        });
      });
      lastSurfaceSettle = NaN;
    }

    function render() {
      frame = 0;
      const bounds = gallery.getBoundingClientRect();
      const { viewport, origin } = kyniqoMetrics(scroller);
      const length = Math.max(1, bounds.height - viewport);
      const progress = reduced.matches ? 1 : clamp((origin - bounds.top) / length);
      const journey = clamp(progress / .83) * (cards.length - 1);
      const finale = ease(clamp((progress - .82) / .08));
      const settle = ease(clamp((progress - .9) / .09));
      const heading = ease(clamp((progress - .91) / .07));
      const intro = 1 - ease(clamp(progress / .1));
      const mobile = window.innerWidth < 700;
      const focused = Math.min(cards.length - 1, Math.round(journey));
      const orbitLayout = [
        [-20,-9],[4,1],[24,-20],[-29,18],[11,-5],[30,22],
        [-31,-24],[-3,20],[27,-12],[-25,8],[7,2],[22,27],
        [-18,-16],[28,9],[-7,24]
      ];
      gallery.classList.toggle('is-finale', finale > .98);
      gallery.style.setProperty('--gallery-finale', finale.toFixed(3));
      gallery.style.setProperty('--gallery-heading', heading.toFixed(3));
      gallery.style.setProperty('--gallery-meta', (1 - finale * (1 - settle)).toFixed(3));
      const zoom = mix(mobile ? 1.1 : 1.3, 1, settle);
      const rise = mix(mobile ? -20 : -55, 0, settle);
      const pivotY = -(stage?.clientHeight || window.innerHeight) * .28;
      const updateSurface = !Number.isFinite(lastSurfaceSettle) || Math.abs(settle - lastSurfaceSettle) > .001;
      finaleCards.forEach(card => {
        const layout = finaleLayout.get(card);
        if (!layout) return;
        const { expanded, settled, segments } = layout;
        const stageWidth = stage?.clientWidth || window.innerWidth;
        const stageHeight = stage?.clientHeight || window.innerHeight;
        const horizontalBias = mobile ? 0 : stageWidth * mix(.035, .006, settle);
        const x = mix(expanded.x * (mobile ? 1 : 1.12), settled.x, settle) * zoom + horizontalBias;
        const baseY = mix(expanded.y, settled.y, settle);
        const y = pivotY + (baseY - pivotY) * zoom + rise - stageHeight * (mobile ? .023 : .047) * settle;
        const z = mix(expanded.z, settled.z, settle);
        card.style.transform = `translate3d(calc(-50% + ${x.toFixed(1)}px), calc(-50% + ${y.toFixed(1)}px), ${z.toFixed(1)}px) scale(${zoom.toFixed(3)}) scaleX(${mix(mobile ? 1 : 1.08, 1, settle).toFixed(3)})`;
        if (updateSurface) segments.forEach((segment, index) => {
          const a = expanded.segments[index];
          const b = settled.segments[index];
          segment.style.transform = `translate3d(0, ${mix(a.y, b.y, settle).toFixed(2)}px, ${mix(a.z, b.z, settle).toFixed(2)}px) rotateX(${mix(a.angle, b.angle, settle).toFixed(2)}deg)`;
        });
      });
      if (updateSurface) lastSurfaceSettle = settle;

      cards.forEach((card, i) => {
        const distance = i - journey;
        const magnitude = Math.abs(distance);
        const visibility = distance < 0
          ? clamp((distance + .3) / .25)
          : clamp((5 - distance) / 1.4);
        const focus = Math.exp(-distance * distance * 1.3);
        const orbitX = orbitLayout[i][0] * (1 - .7 * focus) * (mobile ? .9 : 1) - (i === 0 ? 13 * intro : 0);
        const orbitY = orbitLayout[i][1] * (1 - .7 * focus) * (mobile ? .88 : 1) - (i === 0 ? 7 * intro : 0);
        const orbitZ = distance < 0
          ? 80 - distance * 120
          : 80 - distance * 175;
        const orbitScale = distance < 0
          ? mix(mobile ? 1.05 : 1.2, mobile ? .9 : 1, clamp(-distance / .3))
          : mix(mobile ? 1.05 : 1.2, mobile ? .66 : .72, clamp(distance / 3.5)) * (i === 0 ? 1 - .22 * intro : 1);
        const orbitRotate = Math.sin(i * 2.7) * Math.min(magnitude, 2) * 5;
        card.style.transform = `translate3d(calc(-50% + ${orbitX.toFixed(2)}vw), calc(-50% + ${orbitY.toFixed(2)}vh), ${orbitZ.toFixed(1)}px) rotateY(${(-orbitRotate * .65).toFixed(1)}deg) rotateZ(${orbitRotate.toFixed(1)}deg) scale(${orbitScale.toFixed(3)})`;
        card.style.opacity = (visibility * (1 - finale)).toFixed(3);
        card.style.zIndex = String(Math.round(100 - magnitude * 15));
        card.style.setProperty('--float-delay', `${(-i * .37).toFixed(2)}s`);
      });

      if (status) status.textContent = finale > .8
        ? `46 GAMES · ENDLESS PLAY`
        : `${String(focused + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')} · ${cards[focused].dataset.title}`;
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(render);
    }
    window.addEventListener('scroll', schedule, { passive: true, capture: true, signal });
    if (scroller !== window) scroller.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', () => { layoutFinale(); schedule(); }, { signal });
    reduced.addEventListener?.('change', schedule, { signal });
    layoutFinale();
    render();
    instances.set(gallery, () => {
      controller.abort();
      if (frame) cancelAnimationFrame(frame);
      instances.delete(gallery);
    });
  }

  document.querySelectorAll('[data-games-gallery]').forEach(init);
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches?.('[data-games-gallery]')) init(event.target);
    event.target.querySelectorAll?.('[data-games-gallery]').forEach(init);
  });
  document.addEventListener('shopify:section:unload', event => {
    for (const [gallery, destroy] of instances) if (event.target.contains(gallery)) destroy();
  });
})();
